import { useLanguage, API } from "../App";
import { motion, AnimatePresence } from "framer-motion";
import { useState } from "react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { toast } from "sonner";
import axios from "axios";
import { useRecaptchaToken } from "../hooks/useRecaptchaToken";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "./ui/dialog";
import { ExternalLink, Send, Building2, Globe, Mail, User, Package, Check, Shield, Rocket, X } from "lucide-react";

// Complete integration data with detailed descriptions
const integrations = [
  // ERP Integrations
  {
    name: "Sage",
    category: { fr: "Comptabilité & Gestion", es: "Contabilidad y Gestión", en: "Accounting & Management" },
    type: "ERP",
    color: "#00D639",
    logo: "https://customer-assets.emergentagent.com/job_7fe777d6-9bad-4a97-9808-a97a60a006ea/artifacts/0nl2vhn4_Sage_logo.svg.png",
    description: {
      fr: [
        "Sage est l'un des leaders mondiaux des solutions de gestion pour les PME et grandes entreprises. L'intégration avec Documatch permet une synchronisation automatique de tous vos documents comptables, factures et pièces justificatives.",
        "Grâce à cette connexion native, vous pouvez automatiser le rapprochement des factures avec vos écritures comptables, réduire les erreurs de saisie et gagner un temps précieux sur vos processus financiers."
      ],
      es: [
        "Sage es uno de los líderes mundiales en soluciones de gestión para pymes y grandes empresas. La integración con Documatch permite una sincronización automática de todos sus documentos contables, facturas y justificantes.",
        "Gracias a esta conexión nativa, puede automatizar la conciliación de facturas con sus asientos contables, reducir errores de entrada y ahorrar tiempo valioso en sus procesos financieros."
      ],
      en: [
        "Sage is one of the world leaders in management solutions for SMEs and large enterprises. Integration with Documatch enables automatic synchronization of all your accounting documents, invoices and supporting documents.",
        "Thanks to this native connection, you can automate invoice reconciliation with your accounting entries, reduce data entry errors and save valuable time on your financial processes."
      ]
    }
  },
  {
    name: "SAP",
    category: { fr: "ERP Entreprise", es: "ERP Empresarial", en: "Enterprise ERP" },
    type: "ERP",
    color: "#0FAAFF",
    logo: "https://cdn.worldvectorlogo.com/logos/sap-3.svg",
    description: {
      fr: [
        "SAP est le leader mondial des ERP pour les grandes entreprises. L'intégration Documatch-SAP offre une connexion bidirectionnelle complète pour la gestion documentaire au sein de votre écosystème SAP.",
        "Cette intégration permet d'automatiser l'archivage des documents SAP, de centraliser toutes vos pièces justificatives et d'améliorer la traçabilité de vos processus métier avec une conformité totale aux normes européennes."
      ],
      es: [
        "SAP es el líder mundial de ERP para grandes empresas. La integración Documatch-SAP ofrece una conexión bidireccional completa para la gestión documental dentro de su ecosistema SAP.",
        "Esta integración permite automatizar el archivado de documentos SAP, centralizar todos sus justificantes y mejorar la trazabilidad de sus procesos empresariales con total conformidad con las normas europeas."
      ],
      en: [
        "SAP is the world leader in ERP for large enterprises. The Documatch-SAP integration offers a complete bidirectional connection for document management within your SAP ecosystem.",
        "This integration automates SAP document archiving, centralizes all your supporting documents and improves the traceability of your business processes with full compliance with European standards."
      ]
    }
  },
  {
    name: "Oracle",
    category: { fr: "Base de Données & Cloud", es: "Base de Datos y Cloud", en: "Database & Cloud" },
    type: "ERP",
    color: "#F80000",
    logo: "https://cdn.worldvectorlogo.com/logos/oracle-6.svg",
    description: {
      fr: [
        "Oracle propose des solutions de base de données et d'ERP parmi les plus robustes du marché. L'intégration avec Documatch permet une gestion documentaire avancée directement connectée à vos systèmes Oracle.",
        "Bénéficiez d'une synchronisation en temps réel de vos documents, d'une indexation puissante et d'une recherche avancée intégrée à votre environnement Oracle pour une productivité maximale."
      ],
      es: [
        "Oracle ofrece soluciones de base de datos y ERP entre las más robustas del mercado. La integración con Documatch permite una gestión documental avanzada directamente conectada a sus sistemas Oracle.",
        "Benefíciese de una sincronización en tiempo real de sus documentos, indexación potente y búsqueda avanzada integrada en su entorno Oracle para máxima productividad."
      ],
      en: [
        "Oracle offers some of the most robust database and ERP solutions on the market. Integration with Documatch enables advanced document management directly connected to your Oracle systems.",
        "Benefit from real-time document synchronization, powerful indexing and advanced search integrated into your Oracle environment for maximum productivity."
      ]
    }
  },
  {
    name: "Odoo",
    category: { fr: "ERP Open Source", es: "ERP de Código Abierto", en: "Open Source ERP" },
    type: "ERP",
    color: "#875A7B",
    logo: "https://customer-assets.emergentagent.com/job_7fe777d6-9bad-4a97-9808-a97a60a006ea/artifacts/ydv982wv_odoo_logo.png",
    description: {
      fr: [
        "Odoo est la solution ERP open source la plus populaire, utilisée par des millions d'entreprises dans le monde. L'intégration Documatch-Odoo connecte nativement votre gestion documentaire à tous vos modules Odoo.",
        "Centralisez vos factures, bons de commande, contrats et documents RH dans une interface unifiée. Profitez de workflows automatisés entre Odoo et votre solution GED pour une efficacité optimale."
      ],
      es: [
        "Odoo es la solución ERP de código abierto más popular, utilizada por millones de empresas en todo el mundo. La integración Documatch-Odoo conecta nativamente su gestión documental a todos sus módulos Odoo.",
        "Centralice sus facturas, órdenes de compra, contratos y documentos de RRHH en una interfaz unificada. Aproveche workflows automatizados entre Odoo y su solución GED para una eficiencia óptima."
      ],
      en: [
        "Odoo is the most popular open source ERP solution, used by millions of businesses worldwide. The Documatch-Odoo integration natively connects your document management to all your Odoo modules.",
        "Centralize your invoices, purchase orders, contracts and HR documents in a unified interface. Take advantage of automated workflows between Odoo and your DMS solution for optimal efficiency."
      ]
    }
  },
  {
    name: "Oracle NetSuite",
    category: { fr: "ERP Cloud", es: "ERP en la Nube", en: "Cloud ERP" },
    type: "ERP",
    color: "#1B4F72",
    logo: "https://cdn.worldvectorlogo.com/logos/netsuite-1.svg",
    description: {
      fr: [
        "Oracle NetSuite est le premier ERP cloud au monde, offrant une suite complète de gestion financière et opérationnelle. L'intégration avec Documatch apporte une couche de gestion documentaire avancée à votre NetSuite.",
        "Automatisez la capture et l'archivage de vos documents NetSuite, améliorez la conformité réglementaire et accélérez vos processus de clôture comptable grâce à une traçabilité documentaire complète."
      ],
      es: [
        "Oracle NetSuite es el primer ERP cloud del mundo, ofreciendo una suite completa de gestión financiera y operacional. La integración con Documatch aporta una capa de gestión documental avanzada a su NetSuite.",
        "Automatice la captura y archivado de sus documentos NetSuite, mejore el cumplimiento normativo y acelere sus procesos de cierre contable gracias a una trazabilidad documental completa."
      ],
      en: [
        "Oracle NetSuite is the world's leading cloud ERP, offering a complete suite of financial and operational management. Integration with Documatch brings an advanced document management layer to your NetSuite.",
        "Automate the capture and archiving of your NetSuite documents, improve regulatory compliance and accelerate your accounting close processes with complete document traceability."
      ]
    }
  },
  // CRM Integrations
  {
    name: "Salesforce",
    category: { fr: "CRM Leader", es: "CRM Líder", en: "Leading CRM" },
    type: "CRM",
    color: "#00A1E0",
    logo: "https://cdn.worldvectorlogo.com/logos/salesforce-2.svg",
    description: {
      fr: [
        "Salesforce est le CRM n°1 mondial. L'intégration Documatch-Salesforce vous permet d'associer automatiquement vos documents à vos comptes, contacts et opportunités pour une vue 360° de vos clients.",
        "Accédez à tous les contrats, propositions commerciales et documents clients directement depuis Salesforce. Automatisez le classement des documents par compte et suivez l'historique complet de vos échanges documentaires."
      ],
      es: [
        "Salesforce es el CRM n°1 del mundo. La integración Documatch-Salesforce le permite asociar automáticamente sus documentos a sus cuentas, contactos y oportunidades para una visión 360° de sus clientes.",
        "Acceda a todos los contratos, propuestas comerciales y documentos de clientes directamente desde Salesforce. Automatice la clasificación de documentos por cuenta y siga el historial completo de sus intercambios documentales."
      ],
      en: [
        "Salesforce is the world's #1 CRM. The Documatch-Salesforce integration allows you to automatically associate your documents with your accounts, contacts and opportunities for a 360° view of your customers.",
        "Access all contracts, proposals and customer documents directly from Salesforce. Automate document filing by account and track the complete history of your document exchanges."
      ]
    }
  },
  {
    name: "HubSpot",
    category: { fr: "CRM Marketing & Vente", es: "CRM Marketing y Ventas", en: "Marketing & Sales CRM" },
    type: "CRM",
    color: "#FF7A59",
    logo: "https://customer-assets.emergentagent.com/job_5577aad2-a1d8-4595-9afc-d525b5b1b883/artifacts/wkc957jr_HubSpot_Logo.svg.png",
    description: {
      fr: [
        "HubSpot est la plateforme CRM tout-en-un leader pour le marketing, les ventes et le service client. L'intégration avec Documatch enrichit votre HubSpot d'une gestion documentaire professionnelle.",
        "Liez automatiquement vos documents commerciaux aux deals HubSpot, suivez les contrats signés et centralisez tous les documents de vos campagnes marketing. Une intégration parfaite pour les équipes commerciales modernes."
      ],
      es: [
        "HubSpot es la plataforma CRM todo en uno líder para marketing, ventas y servicio al cliente. La integración con Documatch enriquece su HubSpot con una gestión documental profesional.",
        "Vincule automáticamente sus documentos comerciales a los deals de HubSpot, siga los contratos firmados y centralice todos los documentos de sus campañas de marketing. Una integración perfecta para equipos comerciales modernos."
      ],
      en: [
        "HubSpot is the leading all-in-one CRM platform for marketing, sales and customer service. Integration with Documatch enriches your HubSpot with professional document management.",
        "Automatically link your sales documents to HubSpot deals, track signed contracts and centralize all your marketing campaign documents. A perfect integration for modern sales teams."
      ]
    }
  },
  {
    name: "Zoho",
    category: { fr: "CRM Flexible", es: "CRM Flexible", en: "Flexible CRM" },
    type: "CRM",
    color: "#C8202B",
    logo: "https://cdn.worldvectorlogo.com/logos/zoho-1.svg",
    description: {
      fr: [
        "Zoho CRM offre une solution flexible et abordable pour les PME. L'intégration Documatch-Zoho connecte votre gestion documentaire à l'ensemble de la suite Zoho pour une productivité maximale.",
        "Synchronisez vos documents clients entre Zoho CRM et votre GED, automatisez les workflows documentaires et profitez d'une recherche unifiée à travers toutes vos applications Zoho."
      ],
      es: [
        "Zoho CRM ofrece una solución flexible y asequible para las pymes. La integración Documatch-Zoho conecta su gestión documental a toda la suite Zoho para máxima productividad.",
        "Sincronice sus documentos de clientes entre Zoho CRM y su GED, automatice los workflows documentales y aproveche una búsqueda unificada en todas sus aplicaciones Zoho."
      ],
      en: [
        "Zoho CRM offers a flexible and affordable solution for SMEs. The Documatch-Zoho integration connects your document management to the entire Zoho suite for maximum productivity.",
        "Synchronize your customer documents between Zoho CRM and your DMS, automate document workflows and enjoy unified search across all your Zoho applications."
      ]
    }
  },
  {
    name: "DocuSign",
    category: { fr: "Signature & Gestion Contractuelle", es: "Firma y Gestión Contractual", en: "Signature & Contract Management" },
    type: "CRM",
    color: "#FFC700",
    logo: "https://customer-assets.emergentagent.com/job_7fe777d6-9bad-4a97-9808-a97a60a006ea/artifacts/7586lb42_Docusign_Full_Color.svg.png",
    description: {
      fr: [
        "DocuSign est le leader mondial de la signature électronique. L'intégration avec Documatch crée un flux documentaire complet : de la création du document à sa signature et son archivage automatique.",
        "Envoyez des documents à la signature depuis votre GED, récupérez automatiquement les contrats signés et archivez-les avec toutes les métadonnées de signature pour une traçabilité juridique complète."
      ],
      es: [
        "DocuSign es el líder mundial en firma electrónica. La integración con Documatch crea un flujo documental completo: desde la creación del documento hasta su firma y archivado automático.",
        "Envíe documentos a firmar desde su GED, recupere automáticamente los contratos firmados y archívelos con todos los metadatos de firma para una trazabilidad jurídica completa."
      ],
      en: [
        "DocuSign is the world leader in electronic signature. Integration with Documatch creates a complete document flow: from document creation to signing and automatic archiving.",
        "Send documents for signature from your DMS, automatically retrieve signed contracts and archive them with all signature metadata for complete legal traceability."
      ]
    }
  }
];

// Translations for the integration section
const sectionTranslations = {
  fr: {
    title: "Intégrations & Connecteurs",
    subtitle: "Connectez votre GED à vos outils métier",
    ctaTitle: "Prêt à découvrir l'ERP ou CRM idéal pour votre entreprise ?",
    ctaButton: "Contactez un spécialiste"
  },
  es: {
    title: "Integraciones y Conectores",
    subtitle: "Conecte su GED a sus herramientas empresariales",
    ctaTitle: "¿Listo para descubrir el ERP o CRM ideal para tu empresa?",
    ctaButton: "Contactar con un especialista"
  },
  en: {
    title: "Integrations & Connectors",
    subtitle: "Connect your DMS to your business tools",
    ctaTitle: "Ready to discover the ideal ERP or CRM for your business?",
    ctaButton: "Contact a specialist"
  }
};

// Form translations
const formTranslations = {
  fr: {
    modalTitle: "Demande d'intégration – Intégrez votre logiciel",
    modalSubtitle: "Demandez l'intégration de votre solution.",
    benefit1: "Intégration simple et rapide",
    benefit2: "Réponse de notre équipe sous 48 heures",
    instruction: "Remplissez le formulaire ci-dessous.",
    section1: "Informations sur l'entreprise",
    companyName: "Nom de l'entreprise",
    website: "Site web de l'entreprise",
    section2: "Informations sur le logiciel",
    softwareName: "Nom du logiciel",
    category: "Catégorie de la solution",
    categoryPlaceholder: "Sélectionnez une catégorie",
    section3: "Contact",
    firstName: "Prénom",
    lastName: "Nom",
    email: "Email professionnel",
    submit: "Envoyer la demande d'intégration",
    submitting: "Envoi en cours...",
    trust: "Vos informations restent confidentielles. Nous vous contacterons uniquement pour étudier votre demande d'intégration.",
    success: "Votre demande d'intégration a été envoyée avec succès !",
    error: "Une erreur est survenue. Veuillez réessayer.",
    required: "Ce champ est obligatoire",
    invalidEmail: "Email invalide",
    categories: {
      crm: "CRM",
      erp: "ERP",
      dms: "GED / Gestion documentaire",
      billing: "Facturation",
      accounting: "Comptabilité",
      marketing: "Marketing automation",
      hr: "RH / SIRH",
      project: "Gestion de projet",
      other: "Autre"
    }
  },
  es: {
    modalTitle: "Solicitud de integración – Integra tu software",
    modalSubtitle: "Solicite la integración de su solución.",
    benefit1: "Integración simple y rápida",
    benefit2: "Respuesta de nuestro equipo en 48 horas",
    instruction: "Complete el siguiente formulario.",
    section1: "Información de la empresa",
    companyName: "Nombre de la empresa",
    website: "Sitio web de la empresa",
    section2: "Información del software",
    softwareName: "Nombre del software",
    category: "Categoría de la solución",
    categoryPlaceholder: "Seleccione una categoría",
    section3: "Contacto",
    firstName: "Nombre",
    lastName: "Apellido",
    email: "Email profesional",
    submit: "Enviar solicitud de integración",
    submitting: "Enviando...",
    trust: "Su información permanecerá confidencial. Solo la utilizaremos para analizar su solicitud de integración.",
    success: "¡Su solicitud de integración ha sido enviada con éxito!",
    error: "Se produjo un error. Por favor, inténtelo de nuevo.",
    required: "Este campo es obligatorio",
    invalidEmail: "Email inválido",
    categories: {
      crm: "CRM",
      erp: "ERP",
      dms: "Gestión documental / DMS",
      billing: "Facturación",
      accounting: "Contabilidad",
      marketing: "Automatización de marketing",
      hr: "RRHH / HRM",
      project: "Gestión de proyectos",
      other: "Otro"
    }
  },
  en: {
    modalTitle: "Integration Request – Integrate your software",
    modalSubtitle: "Request the integration of your software solution.",
    benefit1: "Simple and fast integration",
    benefit2: "Response from our team within 48 hours",
    instruction: "Fill out the form below to submit your request.",
    section1: "Company Information",
    companyName: "Company name",
    website: "Company website",
    section2: "Software Information",
    softwareName: "Software name",
    category: "Solution category",
    categoryPlaceholder: "Select a category",
    section3: "Contact",
    firstName: "First name",
    lastName: "Last name",
    email: "Professional email",
    submit: "Submit integration request",
    submitting: "Submitting...",
    trust: "Your information will remain confidential. We will only use it to review your integration request.",
    success: "Your integration request has been sent successfully!",
    error: "An error occurred. Please try again.",
    required: "This field is required",
    invalidEmail: "Invalid email",
    categories: {
      crm: "CRM",
      erp: "ERP",
      dms: "Document Management / DMS",
      billing: "Billing",
      accounting: "Accounting",
      marketing: "Marketing automation",
      hr: "HR / HRM",
      project: "Project management",
      other: "Other"
    }
  }
};

// Integration Request Modal Form Component - Single page form
const IntegrationRequestModal = ({ isOpen, onClose, initialLang = "fr" }) => {
  const [formLang, setFormLang] = useState(initialLang);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const getRecaptchaToken = useRecaptchaToken();
  const [formData, setFormData] = useState({
    companyName: "",
    website: "",
    softwareName: "",
    category: "",
    firstName: "",
    lastName: "",
    email: ""
  });

  const t = formTranslations[formLang];

  const validate = () => {
    const newErrors = {};
    if (!formData.companyName.trim()) newErrors.companyName = t.required;
    if (!formData.softwareName.trim()) newErrors.softwareName = t.required;
    if (!formData.category) newErrors.category = t.required;
    if (!formData.firstName.trim()) newErrors.firstName = t.required;
    if (!formData.lastName.trim()) newErrors.lastName = t.required;
    if (!formData.email.trim()) {
      newErrors.email = t.required;
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = t.invalidEmail;
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    
    setIsSubmitting(true);
    
    try {
      const recaptchaToken = await getRecaptchaToken("integration_request");
      await axios.post(`${API}/integration-request`, {
        ...formData,
        language: formLang,
        recaptchaToken
      });
      toast.success(t.success);
      setFormData({ companyName: "", website: "", softwareName: "", category: "", firstName: "", lastName: "", email: "" });
      onClose();
    } catch (error) {
      console.error("Integration request error:", error);
      toast.error(t.error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
          onClick={(e) => e.target === e.currentTarget && onClose()}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
          >
            {/* Header */}
            <div className="bg-gradient-to-r from-blue-600 to-indigo-600 p-6 rounded-t-2xl relative">
              <button
                onClick={onClose}
                className="absolute top-4 right-4 text-white/80 hover:text-white transition-colors"
              >
                <X size={24} />
              </button>
              
              {/* Language Selector */}
              <div className="flex gap-2 mb-4">
                {["fr", "es", "en"].map((lang) => (
                  <button
                    key={lang}
                    onClick={() => setFormLang(lang)}
                    className={`px-3 py-1 rounded-lg text-sm font-semibold transition-all ${
                      formLang === lang
                        ? "bg-white text-blue-600"
                        : "bg-white/20 text-white hover:bg-white/30"
                    }`}
                  >
                    {lang.toUpperCase()}
                  </button>
                ))}
              </div>
              
              <h2 className="text-2xl font-bold text-white mb-2">{t.modalTitle}</h2>
              <p className="text-blue-100">{t.modalSubtitle}</p>
              
              {/* Benefits */}
              <div className="flex flex-wrap gap-4 mt-4">
                <div className="flex items-center gap-2 text-white/90 text-sm">
                  <Check size={16} className="text-green-300" />
                  {t.benefit1}
                </div>
                <div className="flex items-center gap-2 text-white/90 text-sm">
                  <Check size={16} className="text-green-300" />
                  {t.benefit2}
                </div>
              </div>
            </div>

            {/* Single page form */}
            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              <p className="text-slate-600 text-sm">{t.instruction}</p>

              {/* Company Information */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
                  <Building2 size={20} className="text-blue-600" />
                  {t.section1}
                </h3>
                
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-slate-700">{t.companyName} *</Label>
                    <Input
                      value={formData.companyName}
                      onChange={(e) => handleChange("companyName", e.target.value)}
                      placeholder={t.companyName}
                      className={errors.companyName ? "border-red-500" : ""}
                    />
                    {errors.companyName && <p className="text-red-500 text-xs">{errors.companyName}</p>}
                  </div>
                  
                  <div className="space-y-2">
                    <Label className="text-slate-700 flex items-center gap-2">
                      <Globe size={14} className="text-blue-600" />
                      {t.website}
                    </Label>
                    <Input
                      type="url"
                      value={formData.website}
                      onChange={(e) => handleChange("website", e.target.value)}
                      placeholder="https://www.example.com"
                    />
                  </div>
                </div>
              </div>

              {/* Software Information */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
                  <Package size={20} className="text-blue-600" />
                  {t.section2}
                </h3>
                
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-slate-700">{t.softwareName} *</Label>
                    <Input
                      value={formData.softwareName}
                      onChange={(e) => handleChange("softwareName", e.target.value)}
                      placeholder={t.softwareName}
                      className={errors.softwareName ? "border-red-500" : ""}
                    />
                    {errors.softwareName && <p className="text-red-500 text-xs">{errors.softwareName}</p>}
                  </div>
                  
                  <div className="space-y-2">
                    <Label className="text-slate-700">{t.category} *</Label>
                    <Select value={formData.category} onValueChange={(value) => handleChange("category", value)}>
                      <SelectTrigger className={errors.category ? "border-red-500" : ""}>
                        <SelectValue placeholder={t.categoryPlaceholder} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="crm">{t.categories.crm}</SelectItem>
                        <SelectItem value="erp">{t.categories.erp}</SelectItem>
                        <SelectItem value="dms">{t.categories.dms}</SelectItem>
                        <SelectItem value="billing">{t.categories.billing}</SelectItem>
                        <SelectItem value="accounting">{t.categories.accounting}</SelectItem>
                        <SelectItem value="marketing">{t.categories.marketing}</SelectItem>
                        <SelectItem value="hr">{t.categories.hr}</SelectItem>
                        <SelectItem value="project">{t.categories.project}</SelectItem>
                        <SelectItem value="other">{t.categories.other}</SelectItem>
                      </SelectContent>
                    </Select>
                    {errors.category && <p className="text-red-500 text-xs">{errors.category}</p>}
                  </div>
                </div>
              </div>

              {/* Contact */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
                  <User size={20} className="text-blue-600" />
                  {t.section3}
                </h3>
                
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-slate-700">{t.firstName} *</Label>
                    <Input
                      value={formData.firstName}
                      onChange={(e) => handleChange("firstName", e.target.value)}
                      placeholder={t.firstName}
                      className={errors.firstName ? "border-red-500" : ""}
                    />
                    {errors.firstName && <p className="text-red-500 text-xs">{errors.firstName}</p>}
                  </div>
                  
                  <div className="space-y-2">
                    <Label className="text-slate-700">{t.lastName} *</Label>
                    <Input
                      value={formData.lastName}
                      onChange={(e) => handleChange("lastName", e.target.value)}
                      placeholder={t.lastName}
                      className={errors.lastName ? "border-red-500" : ""}
                    />
                    {errors.lastName && <p className="text-red-500 text-xs">{errors.lastName}</p>}
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label className="text-slate-700 flex items-center gap-2">
                    <Mail size={14} className="text-blue-600" />
                    {t.email} *
                  </Label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleChange("email", e.target.value)}
                    placeholder={t.email}
                    className={errors.email ? "border-red-500" : ""}
                  />
                  {errors.email && <p className="text-red-500 text-xs">{errors.email}</p>}
                </div>
              </div>

              {/* Submit */}
              <div className="pt-4 border-t border-slate-100">
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white py-6 text-base"
                >
                  <Rocket size={18} className="mr-2" />
                  {isSubmitting ? t.submitting : t.submit}
                </Button>

                {/* Trust Message */}
                <div className="mt-4 flex items-start gap-2 text-slate-500 text-xs bg-slate-50 p-3 rounded-lg">
                  <Shield size={16} className="text-green-600 flex-shrink-0 mt-0.5" />
                  <p>{t.trust}</p>
                </div>
              </div>
            </form>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

// Integration Card with detailed popup
const IntegrationCard = ({ integration, language, sectionT, onOpenForm }) => {
  const [isOpen, setIsOpen] = useState(false);

  // If it's a partner with landing page, card navigates directly (no popup)
  if (integration.href) {
    return (
      <a
        href={integration.href}
        className="bg-white rounded-xl p-6 shadow-md cursor-pointer flex flex-col items-center gap-3 transition-all duration-300 hover:shadow-xl h-full hover:-translate-y-1 block"
        data-testid={`integration-${integration.name.toLowerCase().replace(/\s+/g, '-')}`}
      >
        {integration.logo ? (
          <div className="w-[84px] h-[84px] rounded-xl flex items-center justify-center bg-white border border-slate-100 p-2">
            <img src={integration.logo} alt={integration.name} className="w-full h-full object-contain" />
          </div>
        ) : (
          <div
            className="w-[84px] h-[84px] rounded-xl flex items-center justify-center text-white font-bold text-2xl shadow-lg"
            style={{ backgroundColor: integration.color }}
          >
            {integration.name.charAt(0)}
          </div>
        )}
        <p className="font-semibold text-slate-700 text-center">{integration.name}</p>
        <p className="text-xs text-slate-500 text-center">{integration.category[language]}</p>
      </a>
    );
  }

  return (
    <>
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogTrigger asChild>
          <motion.div
            whileHover={{ scale: 1.05, y: -5 }}
            className="bg-white rounded-xl p-6 shadow-md cursor-pointer card-hover flex flex-col items-center gap-3 transition-all duration-300 hover:shadow-xl h-full"
            data-testid={`integration-${integration.name.toLowerCase().replace(/\s+/g, '-')}`}
          >
            {integration.logo ? (
              <div className="w-[84px] h-[84px] rounded-xl flex items-center justify-center bg-white border border-slate-100 p-2">
                <img src={integration.logo} alt={integration.name} className="w-full h-full object-contain" />
              </div>
            ) : (
              <div
                className="w-[84px] h-[84px] rounded-xl flex items-center justify-center text-white font-bold text-2xl shadow-lg"
                style={{ backgroundColor: integration.color }}
              >
                {integration.name.charAt(0)}
              </div>
            )}
            <p className="font-semibold text-slate-700 text-center">{integration.name}</p>
            <p className="text-xs text-slate-500 text-center">{integration.category[language]}</p>
          </motion.div>
        </DialogTrigger>
        <DialogContent className="sm:max-w-lg bg-white max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3 text-slate-900">
              <div
                className="w-12 h-12 rounded-xl flex items-center justify-center text-white font-bold text-lg"
                style={{ backgroundColor: integration.color }}
              >
                {integration.name.charAt(0)}
              </div>
              <div>
                <span className="text-xl">{integration.name}</span>
                <p className="text-sm font-normal text-slate-500">{integration.category[language]}</p>
              </div>
            </DialogTitle>
          </DialogHeader>
          
          <div className="py-4 space-y-4">
            {integration.description[language].map((paragraph, idx) => (
              <p key={idx} className="text-slate-600 leading-relaxed">{paragraph}</p>
            ))}
          </div>

          {/* CTA Section with gradient */}
          <div className="mt-6 rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 p-6 text-center">
            <p className="text-white font-semibold mb-4 text-lg">
              {sectionT.ctaTitle}
            </p>
            <Button 
              onClick={() => {
                setIsOpen(false);
                setTimeout(() => onOpenForm(), 300);
              }}
              className="bg-white text-blue-600 hover:bg-blue-50 font-semibold px-6 py-3 animate-pulse"
            >
              <ExternalLink size={18} className="mr-2" />
              {sectionT.ctaButton}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export const Integrations = () => {
  const { language, t } = useLanguage();
  const sectionT = sectionTranslations[language] || sectionTranslations.fr;
  const [isFormOpen, setIsFormOpen] = useState(false);

  const erpIntegrations = integrations.filter(i => i.type === "ERP");
  const crmIntegrations = integrations.filter(i => i.type === "CRM");

  return (
    <div className="max-w-7xl mx-auto" data-testid="integrations-section">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        className="text-center mb-12"
      >
        <h2 className="font-playfair text-3xl md:text-4xl font-bold text-white mb-4">
          {sectionT.title}
        </h2>
        <p className="text-slate-300 text-lg mb-2">{sectionT.subtitle}</p>
        <p className="text-slate-400 text-sm">{t.integrations?.trust || sectionT.subtitle}</p>
      </motion.div>

      {/* ERP Section */}
      <motion.div
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        className="mb-10"
      >
        <h3 className="text-xl font-semibold text-white mb-6 flex items-center gap-2">
          <span className="bg-blue-500 text-white px-3 py-1 rounded-full text-sm">ERP</span>
          Enterprise Resource Planning
        </h3>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4 md:gap-6 items-stretch">
          {erpIntegrations.map((integration, index) => (
            <motion.div
              key={integration.name}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
            >
              <IntegrationCard 
                integration={integration} 
                language={language} 
                sectionT={sectionT}
                onOpenForm={() => setIsFormOpen(true)}
              />
            </motion.div>
          ))}
        </div>
      </motion.div>

      {/* CRM Section */}
      <motion.div
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
      >
        <h3 className="text-xl font-semibold text-white mb-6 flex items-center gap-2">
          <span className="bg-purple-500 text-white px-3 py-1 rounded-full text-sm">CRM</span>
          Customer Relationship Management
        </h3>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 md:gap-6 items-stretch">
          {crmIntegrations.map((integration, index) => (
            <motion.div
              key={integration.name}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
            >
              <IntegrationCard 
                integration={integration} 
                language={language} 
                sectionT={sectionT}
                onOpenForm={() => setIsFormOpen(true)}
              />
            </motion.div>
          ))}
        </div>
      </motion.div>

      {/* Integration Request Modal */}
      <IntegrationRequestModal 
        isOpen={isFormOpen} 
        onClose={() => setIsFormOpen(false)} 
        initialLang={language}
      />
    </div>
  );
};

export default Integrations;
